import type { UsageMeterEventName } from "./UsageMeter";
import type { AppEnvironment } from "./UtilTypes";

export interface Product {
  id: string;
  productId: Record<AppEnvironment, string>;
  type: "payment" | "setup" | "subscription";
  name: string;
}

/**
 * Product IDs are chosen by the repo (Stripe accepts an `id` on product creation) so the admin Stripe
 * setup provisions the same catalogue in test and live mode without per-environment overrides.
 */
export const PRODUCTS = {
  assetSearch: {
    id: "assetSearch",
    productId: {
      local: "prod_calmlens_asset_search",
      dev: "prod_calmlens_asset_search",
      main: "prod_calmlens_asset_search",
    },
    type: "subscription",
    name: "AI Asset Search",
  },
  assetMediaSearchIndexing: {
    id: "assetMediaSearchIndexing",
    productId: {
      local: "prod_calmlens_asset_media_search_indexing",
      dev: "prod_calmlens_asset_media_search_indexing",
      main: "prod_calmlens_asset_media_search_indexing",
    },
    type: "subscription",
    name: "AI Video & Audio Indexing",
  },
  imageTransformed: {
    id: "imageTransformed",
    productId: {
      local: "prod_calmlens_image_transformed",
      dev: "prod_calmlens_image_transformed",
      main: "prod_calmlens_image_transformed",
    },
    type: "subscription",
    name: "Image Transformed",
  },
  websiteIngestion: {
    id: "websiteIngestion",
    productId: {
      local: "prod_calmlens_website_ingestion",
      dev: "prod_calmlens_website_ingestion",
      main: "prod_calmlens_website_ingestion",
    },
    type: "subscription",
    name: "Website Ingestion",
  },
  textClassificationTokens: {
    id: "textClassificationTokens",
    productId: {
      local: "prod_calmlens_text_classification_tokens",
      dev: "prod_calmlens_text_classification_tokens",
      main: "prod_calmlens_text_classification_tokens",
    },
    type: "subscription",
    name: "Text Classification",
  },
  imageClassification: {
    id: "imageClassification",
    productId: {
      local: "prod_calmlens_image_classification",
      dev: "prod_calmlens_image_classification",
      main: "prod_calmlens_image_classification",
    },
    type: "subscription",
    name: "Image Classification",
  },
  assetStorageGb: {
    id: "assetStorageGb",
    productId: {
      local: "prod_calmlens_asset_storage_gb",
      dev: "prod_calmlens_asset_storage_gb",
      main: "prod_calmlens_asset_storage_gb",
    },
    type: "subscription",
    name: "Asset Storage",
  },
  assetRead: {
    id: "assetRead",
    productId: {
      local: "prod_calmlens_asset_read",
      dev: "prod_calmlens_asset_read",
      main: "prod_calmlens_asset_read",
    },
    type: "subscription",
    name: "Asset Read",
  },
  ocrClassification: {
    id: "ocrClassification",
    productId: {
      local: "prod_calmlens_ocr_classification",
      dev: "prod_calmlens_ocr_classification",
      main: "prod_calmlens_ocr_classification",
    },
    type: "subscription",
    name: "OCR Classification",
  },
  personDetection: {
    id: "personDetection",
    productId: {
      local: "prod_calmlens_person_detection",
      dev: "prod_calmlens_person_detection",
      main: "prod_calmlens_person_detection",
    },
    type: "subscription",
    name: "Person Detection",
  },
  faceDetection: {
    id: "faceDetection",
    productId: {
      local: "prod_calmlens_face_detection",
      dev: "prod_calmlens_face_detection",
      main: "prod_calmlens_face_detection",
    },
    type: "subscription",
    name: "Face Detection",
  },
} as const satisfies Record<string, Product>;

export const USAGE_PRODUCTS: Record<UsageMeterEventName, Product> = {
  image_transformed: PRODUCTS.imageTransformed,
  website_ingestion: PRODUCTS.websiteIngestion,
  //multimedia_document_ingestion: PRODUCTS.websiteIngestion,
  text_classification_tokens: PRODUCTS.textClassificationTokens,
  image_classification: PRODUCTS.imageClassification,
  asset_storage_mb_hours: PRODUCTS.assetStorageGb,
  asset_search: PRODUCTS.assetSearch,
  asset_media_search_indexing: PRODUCTS.assetMediaSearchIndexing,
  asset_read: PRODUCTS.assetRead,
  ocr_classification: PRODUCTS.ocrClassification,
  person_detection: PRODUCTS.personDetection,
  face_detection: PRODUCTS.faceDetection,
};

// The schema lives in its own module so product constants (used on the landing page) don't load zod.
export type { Subscription } from "./SubscriptionSchema";

export const FREE_UPLOAD_LIMIT = 100;
/** Largest text or document file a free upload (made before billing is set up) may be. */
export const FREE_UPLOAD_MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
export const FREE_UPLOAD_MAX_DOCUMENT_STRING = "10 MiB";
/**
 * Most text tokens one free upload may have moderated, checked once its text is extracted: a
 * document's text can far outgrow its file.
 */
export const FREE_UPLOAD_MAX_TEXT_TOKENS = 100_000;
export const MINIMUM_TEXT_CLASSIFICATION_BILLABLE_TOKENS = 250;

export const getTextClassificationBillableTokens = (tokenCount: number) =>
  Math.max(MINIMUM_TEXT_CLASSIFICATION_BILLABLE_TOKENS, Math.ceil(tokenCount));

/**
 * Version of the usage subscription configuration.
 * When this changes, all subscriptions will be updated to match the new product/price configuration.
 */
export const USAGE_SUBSCRIPTION_VERSION = "15";
